import { useState, useEffect, useCallback } from 'react';
import { APP_VERSION } from '@/config/version';

/**
 * Cookie Consent Interface
 *
 * Stores user's cookie preferences in localStorage
 *
 * @property technical - Always true (technical cookies don't require consent)
 * @property analytics - true if user accepts analytics cookies, false otherwise
 * @property timestamp - ISO 8601 string when consent was given
 * @property version - App version when consent was given (for tracking policy changes)
 */
export interface CookieConsent {
  technical: boolean;
  analytics: boolean;
  timestamp: string;
  version: string;
}

/**
 * LocalStorage key for cookie consent
 */
const CONSENT_STORAGE_KEY = 'trackv16-cookie-consent';

/**
 * Custom hook for managing cookie consent
 *
 * Features:
 * - Persists consent in localStorage
 * - Detects first-time users (shows banner)
 * - Provides functions to save/get/revoke consent
 * - Tracks app version for policy changes
 *
 * Usage:
 * ```tsx
 * const { consent, showBanner, saveConsent, revokeConsent } = useCookieConsent();
 *
 * if (showBanner) {
 *   return <CookieBanner onAccept={() => saveConsent(true)} />;
 * }
 * ```
 *
 * @returns {Object} Cookie consent state and methods
 */
export const useCookieConsent = () => {
  const [consent, setConsent] = useState<CookieConsent | null>(null);
  const [showBanner, setShowBanner] = useState(false);

  /**
   * Get consent from localStorage
   *
   * @returns Parsed consent object or null if not found
   */
  const getConsent = useCallback((): CookieConsent | null => {
    try {
      const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (!stored) return null;

      const parsed = JSON.parse(stored) as CookieConsent;

      // Validate structure
      if (
        typeof parsed.technical !== 'boolean' ||
        typeof parsed.analytics !== 'boolean' ||
        !parsed.timestamp ||
        !parsed.version
      ) {
        console.warn('Invalid consent structure, removing from localStorage');
        localStorage.removeItem(CONSENT_STORAGE_KEY);
        return null;
      }

      return parsed;
    } catch (error) {
      console.error('Error reading cookie consent from localStorage:', error);
      return null;
    }
  }, []);

  /**
   * Load consent from localStorage on mount
   */
  useEffect(() => {
    const savedConsent = getConsent();

    if (savedConsent) {
      setConsent(savedConsent);
      setShowBanner(false);
    } else {
      // First-time user: show banner
      setShowBanner(true);
    }
  }, [getConsent]);

  /**
   * Save consent to localStorage
   *
   * @param acceptAnalytics - true to accept analytics cookies, false for technical only
   */
  const saveConsent = useCallback((acceptAnalytics: boolean) => {
    const newConsent: CookieConsent = {
      technical: true, // Always true (technical cookies are mandatory)
      analytics: acceptAnalytics,
      timestamp: new Date().toISOString(),
      version: APP_VERSION,
    };

    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(newConsent));
      setConsent(newConsent);
      setShowBanner(false);
    } catch (error) {
      console.error('Error saving cookie consent to localStorage:', error);
    }
  }, []);

  /**
   * Revoke consent (delete from localStorage)
   *
   * This will show the banner again on next page load
   */
  const revokeConsent = useCallback(() => {
    try {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
      setConsent(null);
      setShowBanner(true);
    } catch (error) {
      console.error('Error revoking cookie consent:', error);
    }
  }, []);

  /**
   * Check if user has given consent (any type)
   */
  const hasConsent = useCallback((): boolean => {
    return consent !== null;
  }, [consent]);

  /**
   * Check if analytics cookies are enabled
   */
  const analyticsEnabled = useCallback((): boolean => {
    return consent?.analytics === true;
  }, [consent]);

  return {
    consent,
    showBanner,
    saveConsent,
    revokeConsent,
    getConsent,
    hasConsent,
    analyticsEnabled,
  };
};
